import { supabase } from '@/lib/supabase';
import type { LocationCoordinate, AddressInfo, SavedAddress, StorageData } from './location.types';

export const STORAGE_KEY = 'ecodeal-location-data';
export const MAX_SAVED_ADDRESSES_GUEST = 5; // 비로그인 유저는 최대 5개 주소만 저장
export const STORAGE_EXPIRY_DAYS = 7; // 7일 후 자동 삭제

// 현재 로그인 사용자 확인 헬퍼 함수
export const getCurrentUser = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  return user;
};

export const loadFromStorage = () => {
  // 게스트 사용자용 localStorage 로드 함수
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const data: StorageData = JSON.parse(stored);
      
      // 데이터 만료 확인 (비로그인 유저만)
      if (data.isGuest && data.lastUpdated) {
        const lastUpdated = new Date(data.lastUpdated);
        const now = new Date();
        const daysDiff = (now.getTime() - lastUpdated.getTime()) / (1000 * 3600 * 24);
        
        if (daysDiff > STORAGE_EXPIRY_DAYS) {
          // 만료된 데이터 삭제
          localStorage.removeItem(STORAGE_KEY);
          return null;
        }
      }
      
      // 선택된 주소가 있으면 currentLocation에 반영
      let currentLocation = data.currentLocation || { coordinate: null, address: null };
      if (data.selectedAddressId && data.savedAddresses) {
        const selectedAddress = data.savedAddresses.find(addr => addr.id === data.selectedAddressId);
        if (selectedAddress) {
          currentLocation = {
            coordinate: selectedAddress.coordinate,
            address: {
              roadAddress: selectedAddress.detailAddress 
                ? `${selectedAddress.address} ${selectedAddress.detailAddress}`
                : selectedAddress.address,
              jibunAddress: selectedAddress.detailAddress
                ? `${selectedAddress.jibunAddress} ${selectedAddress.detailAddress}`
                : selectedAddress.jibunAddress,
              zonecode: selectedAddress.additionalInfo
            }
          };
        }
      }
      
      return {
        currentLocation,
        savedAddresses: data.savedAddresses || [],
        selectedAddressId: data.selectedAddressId || null
      };
    }
  } catch (error) {
    console.error('Failed to load location data from storage:', error);
    // 파싱 에러 시 기존 데이터 삭제
    localStorage.removeItem(STORAGE_KEY);
  }
  return null;
};

export const saveToStorage = async (state: {
  currentLocation: { coordinate: LocationCoordinate | null; address: AddressInfo | null };
  savedAddresses: SavedAddress[];
  selectedAddressId: string | null;
}) => {
  // 게스트 사용자용 localStorage 저장 함수
  const user = await getCurrentUser();
  if (user) {
    // 로그인 사용자는 DB에 저장되므로 localStorage 저장 불필요
    return;
  }
  
  try {
    const isGuest = true;
    
    const dataToSave: StorageData = {
      currentLocation: state.currentLocation,
      savedAddresses: state.savedAddresses,
      selectedAddressId: state.selectedAddressId,
      lastUpdated: new Date().toISOString(),
      isGuest
    };
    
    // localStorage 크기 제한 확인 (5MB 제한)
    const dataSize = JSON.stringify(dataToSave).length;
    if (dataSize > 5 * 1024 * 1024) { // 5MB 초과 시
      console.warn('Location data too large, skipping save');
      return;
    }
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave));
  } catch (error) {
    if (error instanceof DOMException && (error.name === 'QuotaExceededError' || error.code === 22)) {
      // localStorage 할당량 초과
      console.warn('localStorage quota exceeded, clearing old data');
      clearLocationData();
    } else {
      console.error('Failed to save location data to storage:', error);
    }
  }
};

export const clearLocationData = () => {
  localStorage.removeItem(STORAGE_KEY);
};